import { ComponentBase } from './ComponentBase';
import { template, unsafeHTML } from '../utils/TemplateParser';
import { Dropdown } from '../utils/Dropdown';
import { vars } from '../utils/vars';
import { Emitters } from '../utils/Emitters';
import { lang } from '../lang';
import MCDatepicker from '../../mc-datepicker/mc-calendar.min.js';
import { DateTime } from 'luxon';

export class Controls extends ComponentBase {
    constructor(options = {}) {
        super({class: 'controls'});

        this.viewSelector = options.viewSelector ?? true;
        this.action = options.action ?? 'filter';

        this.filterOptions = {
            featured: (vars.pubcalendar.eventType == 'FEATURED' && !options.query) || null,
            date: null,
            tag: [],
            query: options.query ? this.decode(options.query) : null,
        };

        if (options.filters) {
            const filters = JSON.parse(atob(options.filters));
            this.filterOptions = {
                ...this.filterOptions,
                ...filters
            }
        } else if (options.tag) {
            this.filterOptions = { tag: this.decode(options.tag).split(',') };
        }
    }
    registerEvents() {
        return {
            'click    .mobile-filter'                   : 'openMobileFilter',
            'click    .mobile-filter-close button'      : 'closeMobileFilter',
            'click    .mobile-filter-actions .apply'    : 'closeMobileFilter',
            'click    .mobile-filter-actions .clear'    : 'clearMobileFilters',
            'click    .mobile-filter-actions .close'    : 'closeMobileFilter',
            'open     .filter-by-date'                  : 'openDatePicker',
            'close    .filter-by-date'                  : 'closeDatePicker',
            'keydown'                                   : 'keyboard',

            'click    .filter-by-type .dropdown-content .dropdown-item' : 'setType',
            'click    .filter-by-tag .dropdown-content .dropdown-item' : 'toggleDropdownOption',
            'click    a[data-action="routeChange"]' : 'handleRouteChange',
        };
    }
    template() {
        return template`
            ${this.pageTitleTemplate()}

            <button class="mobile-filter" aria-label="${lang.OpenFilterOptions}" aria-haspopup="true" aria-expanded="false" aria-controls="filter-group"><i class="icon far fa-sliders-h"></i> ${lang.Filter}</button>

            <div class="filter-group" id="filter-group">
                <div class="mobile-filter-close"><button aria-label="${lang.CloseFilterOptions}" aria-controls="filter-group"><i class="icon fas fa-times"></i></button> ${lang.FilterEvents}</div>

                ${this.dropdownTemplate({
                    label: lang.EventType,
                    options: [lang.AllEvents, lang.FeaturedEvents],
                    selected: this.filterOptions.featured ? [lang.FeaturedEvents] : [lang.AllEvents],
                    id: 'filter-by-type',
                    })
                }

                <div class="dropdown filter-by-date">
                    <button class="dropdown-trigger" id="filter-by-date" aria-haspopup="true" aria-expanded="false"> <i class="icon far fa-angle-down"></i> ${lang.Date}</button>
                    <div class="dropdown-menu">
                        <div class="dropdown-content">
                            <label for="date-picker">${lang.ChooseADate}</label>
                            <input id="date-picker" type="text" value="${this.filterOptions.date ? this.filterOptions.date : ''}" />
                        </div>
                    </div>
                </div>

                ${this.dropdownTemplate({
                    label: lang.Categories,
                    emptyLabel: lang.NoCategories,
                    options: this.tagCollection, // provided by Events List
                    selected: this.filterOptions.tag,
                    id: 'filter-by-tag',
                    })
                }

                <div class="mobile-filter-actions mobile-only">
                    <div>
                        <button class="apply" aria-label="${lang.ApplyFilterOptions}" aria-controls="filter-group">${lang.Apply}</button>
                        <button class="clear" aria-label="${lang.ClearFilterOptions}" aria-controls="filter-group">${lang.Clear}</button>
                        <button class="close" aria-label="${lang.CloseFilterOptions}" aria-controls="filter-group">${lang.Close}</button>
                    </div>
                </div>
                ${this.viewSelectorTemplate()}
            </div>
        `;
    }
    pageTitleTemplate() {
        const title = this.action == 'link' ? template`<a href="${this.getRouteURL('')}" data-action="routeChange">${vars.pubcalendar.title}</a>` : this.getPageTitle();
        return template`<h2 class="page-title" id="page-title">${title}</h2>`;
    }
    dropdownTemplate({label, emptyLabel, options, id, selected = []}) {
        function row(option, selected) {
            const ariaSelected = selected.includes(option);
            return template`<a role="option" aria-selected="${ariaSelected}" tabindex="0" class="dropdown-item" data-value="${option}">${option}</a>`;
        }

        const content = options.length ? options.map(option => row(option, selected)) : template`<span class='text-center'>${emptyLabel}</span>`;

        return template`
            <div class="dropdown ${id}">
                <button class="dropdown-trigger" id="${id}" aria-haspopup="listbox" aria-expanded="false"> <i class="icon far fa-angle-down"></i> ${label}</button>
                <div class="dropdown-menu">
                    <div class="dropdown-content" role="listbox" aria-labelledby="${id}">
                        ${content}
                    </div>
                </div>
            </div>
        `;
    }
    viewSelectorTemplate() {
        if (!this.viewSelector) {
            return '';
        }
        return template`
            <div class="view-selector" role="listbox" aria-label="${lang.SelectEventViewType}">
                <button role="option" aria-selected="true" aria-label="${lang.ListView}" title="${lang.ListView}" data-action="show-list"><i class="icon far fa-list-alt"></i></button>
                <button role="option" aria-selected="false" aria-label="${lang.CalendarView}" title="${lang.CalendarView}" data-action="show-grid"><i class="icon far fa-th"></i></button>
            </div>
        `;
    }

    ticketTemplate(data) {
        const ticketPrice = this.ticketPriceFormatted(data);
        const ticketLink = this.ticketLinkTemplate(data);
        let ticketInfo;

        if(ticketPrice && ticketLink) {
            ticketInfo = template`<div class="bold">${ticketPrice}</div>${ticketLink}`;
        } else if(ticketPrice) {
            ticketInfo = template`<div class="bold">${ticketPrice}</div>`;
        } else if(ticketLink) {
            ticketInfo = template`<div>${ticketLink}</div>`;
        }

        if(!ticketInfo) return '';

        return template`<div class="detail-with-icon tickets" aria-label="${lang.EventTickets}"><i class="icon fas fa-ticket-alt"></i> <div>${ticketInfo}</div></div>`;
    }

    ticketPriceFormatted(data) {
        const {
            ticketOption,
            ticketCurrency,
            ticketPriceMinimum,
            ticketPriceMaximum,
        } = data;

        if(ticketOption == 'FREE') return lang.Free;
        if(!ticketPriceMinimum) return '';

        const currencySymbol = lang.currencySymbols[ticketCurrency];
        let formattedPrice = `${currencySymbol}${ticketPriceMinimum}`;
        if(ticketPriceMaximum) formattedPrice += ` - ${currencySymbol}${ticketPriceMaximum}`;
        if(ticketCurrency == 'CAD') formattedPrice += ` (${ticketCurrency})`;

        return unsafeHTML(formattedPrice);
    }

    ticketLinkTemplate(data) {
        const {
            ticketButtonLabel,
            ticketUrl,
        } = data;

        if(!ticketUrl || !ticketButtonLabel) return '';
        return template`<a href="${ticketUrl}" title="${ticketButtonLabel}" target="_blank">${ticketButtonLabel}</a>`;
    }

    domReady() {
        const datePickerDropdown = this.el.querySelector('.filter-by-date');
        const datePickerConfig = {
            el: '#date-picker',
            context: vars.calendarShadowDom.querySelector('#omnicms-calendar-wrapper'),
            bodyType: 'inline',
            dateFormat: 'yyyy-mm-dd',
            autoClose: true,
        };
        if (this.filterOptions.date) {
            const date = DateTime.fromISO(this.filterOptions.date, {zone: 'UTC'});
            datePickerConfig.selectedDate = new Date(date.year, date.month - 1, date.day);
        }
        this.datePicker = MCDatepicker.create(datePickerConfig);

        // Close dropdown when date picker is closed
        this.datePicker.onClose(() => {
            Dropdown.close(datePickerDropdown);
        });
        this.datePicker.onSelect(() => {
            this.clearFeatured();
            this.updateFilterOptions();
            if(this.filterOptions.date) Emitters.dispatchDatepickerSelect(vars.viewContainer, { date: this.filterOptions.date });
        });
        this.datePicker.onClear(() => {
            this.datePicker.close();
            this.updateFilterOptions();
            Emitters.dispatchDatepickerClear(vars.viewContainer);
        });
        this.datePicker.onOpen((e) => {
            const cal = vars.calendarShadowDom.querySelector('.mc-calendar');
            const calPosition = cal.getBoundingClientRect().left;
            const inputPosition = this.elements.dateInput.getBoundingClientRect().left;
            const menuPosition = this.elements.dateInput.closest('.dropdown-menu').getBoundingClientRect();
            const offset = 5;
            const shadowPosition = vars.calendarElement.getBoundingClientRect();
            if (calPosition < inputPosition - offset) {
                // If date picker would be partially off-screen, it ends up in a strange place. Recalculate its position
                cal.style.left = `${menuPosition.right - cal.clientWidth}px`;
            } else {
                // Date picker isn't off screen - reset horizontal position to account for shadow dom
                cal.style.left = `${menuPosition.left - shadowPosition.left}px`;
            }
            // Always reset vertical position to account for shadow dom
            cal.style.top = `${menuPosition.top - vars.calendarElement.getBoundingClientRect().top}px`;
            this.elements.datePicker.querySelector('[tabindex="0"]').focus();
        });

        this.elements = {
            type: this.el.querySelector('.filter-by-type'),
            date: this.el.querySelector('.filter-by-date'),
            dateInput: this.el.querySelector('#date-picker'),
            tags: this.el.querySelector('.filter-by-tag'),
            pageTitle: this.el.querySelector('.page-title'),
            datePicker: vars.calendarShadowDom.querySelector('.mc-calendar'),
            searchInput: vars.calendarShadowDom.querySelector('[data-action="event-search"] input[type="search"]'),
        }

        // Set active filters on load for filter options that are passed into Controls
        this.updateActiveFilters();
    }

    updateFilterOptions() {
        this.filterOptions.featured = this.elements.type.querySelector(`[data-value="${lang.FeaturedEvents}"][aria-selected="true"]`) ? true : null;
        this.filterOptions.date = this.datePicker.getFormatedDate();
        this.filterOptions.tag = [...this.elements.tags.querySelectorAll('[aria-selected="true"]')].map(element => {
            return element.getAttribute('data-value');
        });

        const tagFilterOnly = this.filterOptions.tag.length && !this.filterOptions.date && !this.filterOptions.featured;
        let eventsRoute = this.getRouteURL(`events/tag/${this.encode(this.filterOptions.tag.join(','))}`);

        if (this.action == 'link') {
            // Redirect to page
            if(!tagFilterOnly) {
                const filterString = btoa(JSON.stringify(this.filterOptions)); // Base64 encode filters
                eventsRoute = this.getRouteURL(`events/${filterString}`);
            }
            this.triggerRouteChange(eventsRoute);
        } else {
            this.setPageTitle();
            this.updateActiveFilters();
            Emitters.dispatchFilterChange(this.el);
            if(tagFilterOnly) this.triggerRouteChange(eventsRoute, 'replace');
        }
    }

    setPageTitle() {
        this.elements.pageTitle.innerText = this.getPageTitle();
    }
    getPageTitle() {
        if (this.filterOptions.featured) {
            return lang.FeaturedEvents;
        }

        if (this.filterOptions.query) {
            return lang.SearchResultsFor.replace('{{query}}', this.filterOptions.query);
        }
        
        let title = [];
        if (this.filterOptions.date) {
            // No need to convert time zones here because we're not dealing with time
            const date = DateTime.fromISO(this.filterOptions.date, {zone: 'UTC'}).toFormat('EEEE, MMMM d, y');
            title.push(date);
        }

        if (this.filterOptions.tag.length) {
            // only include tags that are associated with events
            const filteredTags = this.filterOptions.tag.filter(tag => this.tagCollection.includes(tag));
            if (filteredTags.length) title.push(filteredTags.join(', '));
        }
            
        if (title.length > 1) {
            return title.join(' — ');
        }
        else if (title.length == 1) {
            return title[0];
        }
        else {
            return lang.AllEvents;
        }
    }

    // Visibly sets dropdown trigger as active
    updateActiveFilters() {
        if (this.filterOptions.tag.length) {
            Dropdown.setActive(this.elements.tags);
        } else {
            Dropdown.setInactive(this.elements.tags);
        }

        if (this.filterOptions.date) {
            Dropdown.setActive(this.elements.date);
        } else {
            Dropdown.setInactive(this.elements.date);
        }
    }

    // Clears all filters except Featured
    clearFilters() {
        for (const el of this.elements.tags.querySelectorAll('[aria-selected="true"]')) {
            el.setAttribute('aria-selected', false);
        }

        this.clearSearch();
        this.datePicker.reset();
    }
    clearFeatured() {
        this.clearSearch();
        if (this.elements.type.querySelector(`[data-value="${lang.FeaturedEvents}"][aria-selected="true"]`)) {
            this.clearTypeFilter();
            this.elements.type.querySelector(`[data-value="${lang.AllEvents}"]`).setAttribute('aria-selected', true);
            Emitters.dispatchFeaturedChange(this.el);
        }
    }
    setFeatured() {
        this.clearSearch();
        if (!this.elements.type.querySelector(`[data-value="${lang.FeaturedEvents}"][aria-selected="true"]`)) {
            this.clearTypeFilter();
            this.elements.type.querySelector(`[data-value="${lang.FeaturedEvents}"]`).setAttribute('aria-selected', true);
            Emitters.dispatchFeaturedChange(this.el);
        }
    }
    clearSearch() {
        delete this.filterOptions.query;
        if(this.elements.searchInput) this.elements.searchInput.value = '';
    }
    clearMobileFilters() {
        this.clearFilters();
        this.updateFilterOptions();
    }

    /**
     * Returns a filterOptions object to be used in api calls
     * @returns {Object}
     */
    getFilterOptions() {
        const copy = {...this.filterOptions};
        for (const prop in copy) {
            if (copy[prop] == null) {
                delete copy[prop];
            }
        }
        return copy;
    }

    /* Event Handlers */
    openMobileFilter(e) {
        const filterGroup = this.el.querySelector(".filter-group");
        filterGroup.style.display = "flex";
        filterGroup.scrollIntoView();
    }
    closeMobileFilter(e) {
        this.el.querySelector(".filter-group").style.removeProperty("display");
    }

    openDatePicker(){
        this.datePicker.open();
    }
    closeDatePicker(){
        this.datePicker.close();
    }

    toggleFeatured(e) {
        if (e.target.getAttribute('aria-pressed') == 'true') {
            this.clearFeatured();
        } else {
            this.setFeatured();
        }
        this.clearFilters();
        this.updateFilterOptions();
    }

    setType(e) {
        const target = e.target;
        if(!target.classList.contains('dropdown-item')) return;
        const value = target.getAttribute('data-value');
        target.closest('.dropdown').classList.remove('is-open'); // closes type filter dropdown
        this.closeMobileFilter();

        if (value == lang.AllEvents) {
            this.clearFeatured();
        } else {
            this.setFeatured();
        }
        this.clearFilters();
        this.updateFilterOptions();
    }

    clearTypeFilter() {
        this.el.querySelectorAll('.filter-by-type .dropdown-item').forEach(e => e.setAttribute('aria-selected', 'false'));
    }

    toggleDropdownOption(e) {
        if (e.target.hasAttribute('aria-selected') && e.target.getAttribute('aria-selected') == 'true') {
            e.target.setAttribute('aria-selected', false);
        } else {
            e.target.setAttribute('aria-selected', true);
        }
        this.clearFeatured();
        this.updateFilterOptions();
    }

    keyboard(e) {
        if (e.key == "Escape") {
            if (e.target.closest('.dropdown-menu')) {
                // close dropdown
                Dropdown.close(e.target.closest('.dropdown'));
            }
            else if (e.target.closest('.mc-calendar')) {
                // close date picker
                this.datePicker.close();
            }
        }
        else if (e.key == "Enter") {
            if (e.target.closest('.filter-by-type')) {
                this.setType(e);
            } else if (e.target.closest('.dropdown-item')) {
                this.toggleDropdownOption(e);
            }
        }
    }

    destroy() {
        this.datePicker?.destroy();
        super.destroy();
    }

}